import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Typography
} from 'antd';
import React, { useEffect, useState } from 'react';
import { getPrices } from '../../services/pricing.service';
import { getTrainers } from '../../services/trainerServices';
import {
  conditionalOptions,
  weekDays,
  cusotmLiveSessionCards,
  formTitleMap,
  communityPlatforms
} from '../../utilities/constants';
import {
  renderConditionalOptions,
  getLiveSessionIndexedFormItemName,
  getLiveSessionFormItemName
} from '../../utilities/formHelpers';
import NotedFormItem from '../Common/NotedFormItem';
import '../Common/Form.css';

const { Text } = Typography;

const CourseOfferForm = ({
  initialValues,
  onSubmit,
  status,
  type,
  visible,
  courses,
  course,
  cohortCourses,
  isCohortCourseClone,
  isCohortCourseEditMode,
  isPricingModalVisible,
  setIsPricingModalVisible
}) => {
  const [form] = Form.useForm();

  const [trainers, setTrainers] = useState([]);

  const [
    cohortCourseSelectedFromDropdown,
    setCohortCourseSelectedFromDropdown
  ] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [currentPriceOption, setCurrentPriceOption] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(course ?? null);

  const [pricingSchemes, setPricingSchemes] = useState([]);
  const [specialPrice, setSpecialPrice] = useState(false);

  const dateFormat = 'Do MMMM YYYY h:mm:ssA Z';

  useEffect(() => {
    if (visible) {
      console.log('Getting the trainer from backend');
      setFetching(true);
      getTrainers(0, 0, { sortBy: 'firstName', sortOrder: 'asc' })
        .then((response) => {
          const trainerList = response.data.paginatedList;
          const data = trainerList.map((trainer) => ({
            label: `${trainer.firstName} ${trainer.lastName}`,
            value: trainer.trainerId
          }));
          setTrainers(data);
        })
        .catch((error) => {
          console.log('Error getting data from the admin backend:', error);
          alert('Getting trainers from the backend failed!');
        });
      setFetching(false);
    }
  }, [visible]);

  useEffect(() => {
    fetchPricingOptions();
  }, []);

  const fetchPricingOptions = async () => {
    await getPrices()
      .then((r) => {
        setPricingSchemes(r.data.paginatedList);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const handleCurrentPriceOption = (value) => {
    setCurrentPriceOption(
      pricingSchemes.find((price) => price._id === value)
    );
    showModal();
  };

  const showModal = () => {
    setIsPricingModalVisible(true);
  };

  const handleOk = () => {
    setIsPricingModalVisible(false);
  };

  const handleCancel = () => {
    if (initialValues.pricingOptions) {
      form.setFieldsValue({
        pricingOptions: initialValues.pricingOptions
      });
      setCurrentPriceOption(initialValues.pricingOptions);
    } else {
      form.setFieldsValue({
        pricingOptions: null
      });
      setCurrentPriceOption(null);
    }
    setIsPricingModalVisible(false);
  };

  const renderPricingOptions = () => {
    return pricingSchemes?.map((item, index) => (
      <Select.Option value={item?._id} key={index}>
        Total: {item?.total} - Deposit: {item?.deposit?.value} ({' '}
        {item?.deposit?.type} )
      </Select.Option>
    ));
  };

  const renderPricePoints = (pricePoints) => {
    return pricePoints?.map((point, index) => (
      <ul key={index}>
        <b>Price {index + 1}</b>
        <div>. Number of students: {point?.number}</div>
        <div>. Discount: {point?.discountValue}</div>
        <div>. Type: {point?.type}</div>
        <br />
      </ul>
    ));
  };

  const renderSessions = (
    sessionType,
    sectionNumber,
    position,
    liveSessionCount
  ) => {
    let response = [];
    const iters = liveSessionCount ?? 1;
    for (let i = 0; i < iters; i++) {
      response.push(
        <>
          <Text strong>{`Session ${i + 1}`}</Text>
          <Form.Item
            label={`${formTitleMap[sessionType]} Link`}
            name={getLiveSessionIndexedFormItemName(
              sessionType,
              sectionNumber,
              position,
              i,
              'liveLink'
            )}>
            <Input />
          </Form.Item>
          <Form.Item
            label={`${formTitleMap[sessionType]} Title`}
            name={getLiveSessionIndexedFormItemName(
              sessionType,
              sectionNumber,
              position,
              i,
              'title'
            )}>
            <Input placeholder="If left empty, default generic title is used" />
          </Form.Item>

          <Form.Item
            label={`${formTitleMap[sessionType]} Start Time (Local)`}
            name={getLiveSessionIndexedFormItemName(
              sessionType,
              sectionNumber,
              position,
              i,
              'startTime'
            )}>
            <DatePicker
              showTime={true}
              showNow={false}
              format={dateFormat}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            label={`End Time (Local)`}
            name={getLiveSessionIndexedFormItemName(
              sessionType,
              sectionNumber,
              position,
              i,
              'endTime'
            )}>
            <DatePicker
              showTime={true}
              showNow={false}
              format={dateFormat}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </>
      );
    }
    return response;
  };

  const rederCountryWisePrice = (countryWisePrice) => {
    return countryWisePrice.map((price) => (
      <div>
        <span>
          <b>Total Local: </b>
        </span>
        <span>
          {`${price.country} - 
          ${price.currency} - 
          ${price.amount}`}
        </span>
      </div>
    ));
  };

  return (
    <Form
      labelCol={{ span: 7 }}
      wrapperCol={{ span: 21 }}
      onFinish={onSubmit}
      initialValues={initialValues}
      form={form}>
      <section>
        <Form.Item
          label="Course"
          name="courseId"
          rules={[{ required: true, message: 'Please input course!' }]}>
          {courses ? (
            <Select
              onChange={(e) => {
                if (cohortCourses.includes(e)) {
                  setCohortCourseSelectedFromDropdown(true);
                  // setCustomCardMetadata(e.customCardMetadata);
                } else {
                  setCohortCourseSelectedFromDropdown(false);
                }
                setSelectedCourse(
                  courses.find((course) => course.value === e)
                );
              }}
              options={courses}
              placeholder={'Select Courses'}
              mode={''}
              notFoundContent={fetching ? <Spin size="small" /> : null}
              filterOption={(input, option) => {
                return (
                  option.label
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                );
              }}
            />
          ) : (
            <Text>{initialValues.courseCode}</Text>
          )}
        </Form.Item>
        <Form.Item
          label="Code"
          name="code"
          rules={[
            {
              required:
                !cohortCourseSelectedFromDropdown && !isCohortCourseClone, //Code is required when a not-cohort course is selected and we're not in Cloning cohort-course state
              message: 'Please input code!'
            }
          ]}>
          {/*Code input is disabled when a not-cohort course is selected, or we are in cloning cohort course state */}
          <Input
            disabled={
              cohortCourseSelectedFromDropdown || isCohortCourseClone
            }
          />
        </Form.Item>

        <Form.Item
          label="Wave"
          name="wave"
          rules={[
            {
              required:
                cohortCourseSelectedFromDropdown ||
                isCohortCourseClone ||
                isCohortCourseEditMode, //wave is required when a cohort course is seleced, or cloning cohort course state
              message: 'Must input Wave!'
            }
          ]}>
          <Input />
        </Form.Item>
        <Form.Item
          label="Group Number"
          name="groupNumber"
          rules={[
            {
              required:
                cohortCourseSelectedFromDropdown ||
                isCohortCourseClone ||
                isCohortCourseEditMode, //group number is required when a cohort course is seleced, or cloning cohort course state
              message: 'Must input Group Number!'
            }
          ]}>
          {/*group number input is only enabled when cohort course is selected */}
          <Input
            disabled={
              !cohortCourseSelectedFromDropdown &&
              !isCohortCourseClone &&
              !isCohortCourseEditMode
            }
          />
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
          rules={[
            {
              required: true,
              message: 'Please input course offer description!'
            }
          ]}>
          <Input />
        </Form.Item>
        <Form.Item
          label="Status"
          name="courseOfferStatus"
          rules={[
            {
              required: true,
              message: 'Please input course offer status!'
            }
          ]}>
          <Select>
            <Select.Option value="Pending">
              Pending - Offer awaiting
            </Select.Option>
            <Select.Option value="Active">
              Active - Offer ongoing
            </Select.Option>
            <Select.Option value="Inactive">
              Inactive - Offer completed
            </Select.Option>
            <Select.Option value="Cancelled">
              Cancelled - Offer withdrawn
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Start Date (Local)"
          name="startDate"
          rules={[
            { required: true, message: 'Please input start date!' }
          ]}>
          <DatePicker
            showTime={true}
            showNow={false}
            format={dateFormat}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label="End Date (Local)"
          name="endDate"
          rules={[{ required: true, message: 'Please input end date!' }]}>
          <DatePicker
            showTime={true}
            showNow={false}
            format={dateFormat}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          label="Frequency"
          name="frequency"
          rules={[
            { required: true, message: 'Please select weekday/s!' }
          ]}>
          <Select
            options={weekDays}
            placeholder={'Select Weekdays'}
            mode={'multiple'}
            filterOption={(input, option) =>
              option.option.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          />
        </Form.Item>

        <Form.Item
          label="is Pre Purchase?"
          name="isPrePurchase"
          rules={[
            {
              required: false,
              message: 'Please choose whether it is pre purchase or not!'
            }
          ]}>
          <Select placeholder="Select if its pre purchase or not!">
            <Select.Option value={true}>True</Select.Option>
            <Select.Option value={false}>False</Select.Option>
          </Select>
        </Form.Item>
        <NotedFormItem
          text={'NEW'}
          note={
            'Setting this as true will trigger PRE_PURCHASE_WITH_DATE, false for else'
          }>
          <Form.Item
            label="Set the release date?"
            name="setReleaseDate"
            rules={[
              {
                required: false,
                message: 'Please choose whether it is pre purchase or not!'
              }
            ]}>
            <Select
              placeholder="Select if its pre purchase or not!"
              defaultValue={true}>
              <Select.Option value={true}>True</Select.Option>
              <Select.Option value={false}>False</Select.Option>
            </Select>
          </Form.Item>
        </NotedFormItem>

        <Form.Item
          label="Max Registration"
          name="maxRegistrations"
          rules={[
            { required: true, message: 'Please input max registrations!' }
          ]}>
          <InputNumber min={0} />
        </Form.Item>

        <Form.Item
          label="Min Registration"
          name="minRegistrations"
          rules={[
            { required: true, message: 'Please input min registrations!' }
          ]}>
          <InputNumber min={0} />
        </Form.Item>
      </section>

      <Divider />
      <Text strong="true">Trainers</Text>
      <section>
        <Form.Item label="Trainer" name="trainers">
          <Select
            notFoundContent={fetching ? <Spin size="small" /> : null}
            options={trainers}
            placeholder={'Select Trainers'}
            mode={'multiple'}
            filterOption={(input, option) =>
              option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          />
        </Form.Item>
      </section>

      <Divider />
      <Text strong="true">Details</Text>
      <section>
        <Form.Item label="Location" name="location">
          <Input />
        </Form.Item>
        <Form.Item label="Zoom Link" name="whereToGo">
          <Input />
        </Form.Item>
        <Form.Item label="Calendar Link" name="calendarLink">
          <Input />
        </Form.Item>
        <Form.Item label="Class Materials" name="classMaterialsLink">
          <Input />
        </Form.Item>
        <Form.Item label="Homework Link" name="whatToBring">
          <Input />
        </Form.Item>
        <Form.Item label="Discord Group Link" name="discordGroupLink">
          <Input />
        </Form.Item>
        <Text>Whatsapp Group Links</Text>
        <Form.List
          name="whatsappGroupLinks"
          rules={[{ required: false, message: 'Add links' }]}>
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <Form.Item
                  label={`Link ${index + 1}`}
                  required={false}
                  key={field.key}>
                  <Form.Item
                    {...field}
                    validateTrigger={['onChange', 'onBlur']}
                    rules={[{ required: false, message: 'Missing link' }]}
                    noStyle>
                    <Input placeholder="option" />
                  </Form.Item>
                  {fields.length > 1 ? (
                    <MinusCircleOutlined
                      className="dynamic-delete-button"
                      onClick={() => remove(field.name)}
                    />
                  ) : null}
                </Form.Item>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  style={{ width: '60%' }}
                  icon={<PlusOutlined />}>
                  Add link
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Form.Item
          label="Community Platforms"
          name="communityPlatforms"
          required={true}>
          <Select
            options={communityPlatforms}
            placeholder={'Select domains'}
            mode="multiple"
            required={true}
            notFoundContent={null}
            filterOption={(input, option) =>
              option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          />
        </Form.Item>

        <Form.Item label="Live Session Region" name="liveRegion">
          <Select placeholder="Select live session timezone">
            <Select.Option value="Asia/Singapore">
              Asia (GMT+8)
            </Select.Option>
            <Select.Option value="America/Los_Angeles">
              America (GMT-7)
            </Select.Option>
          </Select>
        </Form.Item>
        <Form.Item label="Creator Image" name="liveCreatorImage">
          <Input placeholder="If left empty, default image is used" />
        </Form.Item>
        <Form.Item label="Meet Creator Link" name="liveCreatorLink">
          <Input />
        </Form.Item>
        <Form.Item
          label="Meet Creator Start Time (Local)"
          name="liveCreatorStartTime">
          <DatePicker
            showTime={true}
            showNow={false}
            format={dateFormat}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label="Meet Creator End Time (Local)"
          name="liveCreatorEndTime">
          <DatePicker
            showTime={true}
            showNow={false}
            format={dateFormat}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Text strong="true">Custom Live Sessions</Text>

        {selectedCourse && selectedCourse.customCardMetadata && (
          <>
            {'  '}
            <Text strong="true">
              The position of the live sessions that are set in the course.
            </Text>
            <section>
              {selectedCourse.customCardMetadata
                ?.filter((card) =>
                  cusotmLiveSessionCards.includes(card.type)
                )
                .map((card, index) => {
                  return (
                    <>
                      <Text
                        strong
                        style={{
                          color: 'darkRed'
                        }}>{`${formTitleMap[card.type]} in day ${
                        card.sectionNumber
                      }, position ${card.position}`}</Text>

                      <Form.Item
                        label={`type:`}
                        initialValue={`${card.type}`}
                        name={getLiveSessionFormItemName(
                          card.type,
                          card.sectionNumber,
                          card.position,
                          `type`
                        )}>
                        <Input
                          disabled={true}
                          defaultValue={`${card.type}`}
                        />
                      </Form.Item>
                      <Form.Item
                        required={true}
                        initialValue={`${card.sectionNumber}`}
                        label={`Day (Section Number)`}
                        name={getLiveSessionFormItemName(
                          card.type,
                          card.sectionNumber,
                          card.position,
                          `sectionNumber`
                        )}>
                        <Input
                          disabled={true}
                          defaultValue={`${card.sectionNumber}`}
                        />
                      </Form.Item>
                      <Form.Item
                        label={`Postion in day (position)`}
                        initialValue={`${card.position}`}
                        name={getLiveSessionFormItemName(
                          card.type,
                          card.sectionNumber,
                          card.position,
                          `position`
                        )}>
                        <Input
                          disabled={true}
                          defaultValue={`${card.position}`}
                        />
                      </Form.Item>
                      <Form.Item
                        label={`${formTitleMap[card.type]} Image`}
                        name={getLiveSessionFormItemName(
                          card.type,
                          card.sectionNumber,
                          card.position,
                          `image`
                        )}>
                        <Input
                          placeholder="If left empty, default image is used"
                          rules={[
                            {
                              required: true,
                              message: 'Please input course code!'
                            }
                          ]}
                        />
                      </Form.Item>

                      {renderSessions(
                        card.type,
                        card.sectionNumber,
                        card.position,
                        card.liveSessionCount
                      )}

                      <Form.List
                        name={getLiveSessionFormItemName(
                          card.type,
                          card.sectionNumber,
                          card.position,
                          `longDescription`
                        )}
                        rules={[
                          { required: false, message: 'Add Paragaph' }
                        ]}>
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map((field, index) => (
                              <Form.Item
                                label={`Paragraph ${index + 1}`}
                                required={true}
                                key={field.key}>
                                <Form.Item
                                  {...field}
                                  validateTrigger={['onChange', 'onBlur']}
                                  noStyle>
                                  <Input placeholder="option" />
                                </Form.Item>
                                {fields.length > 1 ? (
                                  <MinusCircleOutlined
                                    className="dynamic-delete-button"
                                    onClick={() => remove(field.name)}
                                  />
                                ) : null}
                              </Form.Item>
                            ))}
                            <Form.Item>
                              <Button
                                type="dashed"
                                onClick={() => add()}
                                style={{ width: '60%' }}
                                icon={<PlusOutlined />}>
                                Add Paragraph to Description
                              </Button>
                            </Form.Item>
                          </>
                        )}
                      </Form.List>
                      <Divider />
                    </>
                  );
                })}
            </section>
          </>
        )}
      </section>

      <Divider />
      <Text strong="true">Price</Text>
      <section>
        <Form.Item
          label="Pricing option"
          rules={[{ required: false }]}
          name="pricingOptions">
          <Select
            placeholder="Search pricing option"
            notFoundContent={fetching ? <Spin size="small" /> : null}
            filterOption={false}
            showSearch={true}
            onSearch={fetchPricingOptions}
            dropdownRender={(menu) => <div>{menu}</div>}
            onSelect={(value) => handleCurrentPriceOption(value)}>
            {renderPricingOptions()}
          </Select>
        </Form.Item>
        <Form.Item
          label="Is Special Price?"
          name="isSpecial"
          rules={[{ required: true, message: '' }]}>
          <Select
            options={conditionalOptions}
            defaultValue={false}
            onSelect={(value) => setSpecialPrice(value)}>
            {renderConditionalOptions()}
          </Select>
        </Form.Item>
        <Form.Item
          label="Show price?"
          name="showPrice"
          rules={[{ required: true, message: '' }]}>
          <Select options={conditionalOptions} defaultValue={true}>
            {renderConditionalOptions()}
          </Select>
        </Form.Item>
        <Form.Item
          name="specialMessage"
          rules={[{ required: specialPrice, message: '' }]}
          label={'Special price msg'}>
          <Input placeholder="Special Price Message..." />
        </Form.Item>
      </section>

      <Modal
        title="You are selecting this pricing option"
        visible={isPricingModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={'Confirm'}>
        <div>
          <span>
            <b>Total: </b>
          </span>
          <span>{currentPriceOption?.total}</span>
        </div>
        <div>
          <span>
            <b>Country Wise Price: </b>
          </span>
          <span>
            {currentPriceOption?.countryWisePrice &&
              rederCountryWisePrice(currentPriceOption?.countryWisePrice)}
          </span>
        </div>
        <div>
          <span>
            <b>Type/Tag: </b>
          </span>
          <span>{currentPriceOption?.type}</span>
        </div>

        <div>
          <span>
            <b>Deposit: </b>
          </span>
          <span>{currentPriceOption?.deposit?.value}</span>
        </div>
        <div>
          <span>
            <b>Deposit type: </b>
          </span>
          <span>{currentPriceOption?.deposit?.type}</span>
        </div>
        <div>
          <b>Price points:</b>
        </div>
        <li>{renderPricePoints(currentPriceOption?.pricePoints)}</li>
      </Modal>

      <Divider />
      <Text strong="true">Notes</Text>
      <section>
        <Form.Item name="generalNotes">
          <Input placeholder="Add notes here..." />
        </Form.Item>
      </section>
      {status.type === 'ERROR' ? (
        <Text type="danger">{status.message}</Text>
      ) : null}
      {status.type === 'SUCCESS' ? (
        <Text type="success">Successfully Updated!</Text>
      ) : null}
      {type !== 'Clone' ? (
        <Row justify="end" gutter={12}>
          <Col>
            <Button type="primary" htmlType="submit">
              {type}
            </Button>
          </Col>
        </Row>
      ) : (
        <Row justify="end" gutter={12}>
          <Col>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default CourseOfferForm;
